import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { DashboardLayout } from "@/pages/Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import FilterDropdown from "@/components/FilterDropDowns";
import type { ColumnHeader } from "@/components/MyTable";
import { PatientInfoCard } from "../components/PatientInfoCard";
import {
  getLegacyMonthFilters,
  getLegacyMonthParam,
  LEGACY_DEFAULT_HIDDEN_COLUMN_IDS,
  LEGACY_MONTH_FILTER_OPTIONS,
} from "../legacyTableDefaults";

const readingCountHeaders: ColumnHeader[] = [
  { id: "sourcePatientId", label: "ID", isSortable: false },
  { id: "patientName", label: "Patient Name", isSortable: false },
  { id: "dob", label: "DOB", isSortable: false },
  { id: "totalReadingCount", label: "Total Reading Count", isSortable: false },
  { id: "countMonthYear", label: "Count (Month/Year)", isSortable: false },
];

function getFirstValue(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

const EMPTY_FILTERS = {};
const NOOP = () => {};

function mapReadingCountRows(rows: any[]) {
  return rows.map((count: any) => ({
    ...count,
    sourcePatientId: getFirstValue(count, ["sourcePatientId", "patientId", "patientID"]),
    patientName: getFirstValue(count, ["patientName", "name"]),
    dob: getFirstValue(count, ["dob", "dateOfBirth"]),
    totalReadingCount: getFirstValue(count, [
      "totalReadingCount",
      "readingCount",
      "count",
    ]),
    countMonthYear: getFirstValue(count, [
      "monthKey",
      "countMonthYear",
      "monthYear",
      "month",
    ]),
  }));
}

export default function LegacyReadingCount() {
  const { patientId } = useParams();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState<Record<string, string>>(() => getLegacyMonthFilters());
  const requestParams = useMemo(
    () => ({
      patientId,
      ...getLegacyMonthParam(filters.month),
    }),
    [filters.month, patientId],
  );
  const viewData = useMemo(() => mapReadingCountRows(rows), [rows]);

  return (
    <DashboardLayout activePage="/legacy">
      <div className="space-y-4">
        <PatientInfoCard />
        <TableWithSearch
          heading="Past Medical Records Reading Count"
          url="/legacy-reading-counts"
          params={requestParams}
          apiDataKey="readingCounts"
          fetchCondition={!!patientId}
          universalLoadingSkeleton
          isIncludeSearch={false}
          searchPlaceholder="Search reading count"
          headerExtraControls={
            <FilterDropdown
              filters={filters}
              setFilters={setFilters}
              filtersOptions={LEGACY_MONTH_FILTER_OPTIONS}
              defaultFilters={getLegacyMonthFilters()}
              isHideClearAllFilters
            />
          }
          columnHeaders={readingCountHeaders}
          data={rows}
          viewData={viewData}
          setData={setRows}
          isActionColumn={false}
          filtersOptions={[]}
          handleAction={NOOP}
          filters={EMPTY_FILTERS}
          setFilters={NOOP}
          defaultFilters={getLegacyMonthFilters()}
          clickRow={NOOP}
          isColumnSelection
          columnPreferenceKey="legacy-reading-count-v5"
          defaultHiddenColumnIds={LEGACY_DEFAULT_HIDDEN_COLUMN_IDS}
          isIgnoreSearchParams
        />
      </div>
    </DashboardLayout>
  );
}
